import { useState } from 'react'
import type { Transaction } from '@domain/finance'
import { normalizedText } from '@domain/product'
import { JANELA_AMPLA_DIAS, JANELA_DIAS, reconcile } from '@domain/receipt'
import type { Receipt } from '@domain/receipt'
import { daysBetween } from '@shared/date/civil'
import { formatBRL } from '@shared/money/format'
import { fullDayLabel } from '@ui/finance/day-groups'
import { EscolhaEmTelaCheia } from '@ui/shell/EscolhaEmTelaCheia'

/**
 * Escolher, a partir de uma transação, uma nota que já subiu e está solta.
 *
 * ## É o caminho inverso de `VincularTransacao`, e herda a regra dele
 *
 * *"A conciliação SUGERE, e a pessoa decide."* Aqui também nada é vinculado
 * sozinho, nem com uma candidata só: uma nota grudada na transação errada vira
 * o detalhamento de uma compra que não é aquela, e o número fica plausível.
 *
 * ## As sugestões vêm no topo, e são as mesmas duas camadas
 *
 * `reconcile` é chamada ao contrário — cada nota solta contra esta transação —
 * e devolve a distância em dias quando o valor bate. Até três dias é o mesmo
 * valor e a mesma data; até trinta, o mesmo valor mais longe, dito em voz alta.
 * O resto vem depois, da nota mais recente para a mais antiga.
 *
 * **Por isso a melhor sugestão é o primeiro item**, e é ele que o seletor
 * destaca por padrão: digitar nada e apertar Enter vincula a mais provável.
 *
 * ## Nota sem leitura também aparece
 *
 * Uma foto ainda na fila, ou cuja leitura falhou, não tem loja nem valor — e
 * mesmo assim pode ser a desta compra; quem fotografou sabe. Ela entra no fim,
 * com o estado no lugar do valor.
 */
export function EscolherNota({
  transacao,
  notas,
  onEscolher,
  onFechar,
}: {
  transacao: Transaction
  /** As notas SOLTAS — sem transação. Quem chama filtra. */
  notas: Receipt[]
  onEscolher: (nota: Receipt) => void
  onFechar: () => void
}) {
  const [busca, setBusca] = useState('')
  const termo = normalizedText(busca)

  const alvo = [
    {
      id: transacao.id,
      amountCents: transacao.source.amountCents,
      transactionDate: transacao.source.transactionDate,
    },
  ]
  const opcoes = notas
    .map((nota) => {
      const [candidata] =
        nota.extracted === null
          ? []
          : reconcile(nota.extracted, alvo, daysBetween, JANELA_AMPLA_DIAS)
      return { nota, dias: candidata?.daysApart ?? null }
    })
    .filter(
      ({ nota }) =>
        termo === '' || normalizedText(nota.extracted?.merchantName ?? '').includes(termo),
    )
    .sort((a, b) => camada(a.dias) - camada(b.dias) || (a.dias ?? 0) - (b.dias ?? 0) || dataDe(b.nota).localeCompare(dataDe(a.nota)))

  return (
    <EscolhaEmTelaCheia
      titulo="Nota fiscal"
      rotulo="Escolher a nota fiscal"
      rotuloDaBusca="Buscar nota pela loja"
      placeholder="Buscar pela loja…"
      busca={busca}
      onBuscar={setBusca}
      onFechar={onFechar}
    >
      {opcoes.length === 0 ? (
        <p className="px-2 py-4 text-sm text-[var(--text-muted)]">Nenhuma nota solta com esse nome.</p>
      ) : (
        <ul aria-label="Notas soltas">
          {opcoes.map(({ nota, dias }) => (
            <li key={nota.id}>
              <button
                type="button"
                onClick={() => {
                  onEscolher(nota)
                }}
                className="alvo w-full justify-start gap-2 border-b border-[var(--border)] px-2 text-left text-sm"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {nota.extracted?.merchantName ?? 'Nota sem leitura'}
                  </span>
                  <span className="block truncate text-xs text-[var(--text-faint)]">
                    {detalheDa(nota, dias)}
                  </span>
                </span>
                {nota.extracted?.totalCents != null && (
                  <span className="shrink-0 tabular-nums">{formatBRL(nota.extracted.totalCents)}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </EscolhaEmTelaCheia>
  )
}

/** 0 = mesmo valor e data; 1 = mesmo valor, mais longe; 2 = o resto. */
function camada(dias: number | null): number {
  if (dias === null) return 2
  return dias <= JANELA_DIAS ? 0 : 1
}

/** A data que ordena o resto: a da nota, e a do envio quando não houve leitura. */
function dataDe(nota: Receipt): string {
  return nota.extracted?.issuedAt ?? nota.createdAt.slice(0, 10)
}

function detalheDa(nota: Receipt, dias: number | null): string {
  if (nota.extracted === null) return ESTADO[nota.status]
  const quando = nota.extracted.issuedAt === null ? 'sem data' : fullDayLabel(nota.extracted.issuedAt)
  if (dias === null) return quando
  if (dias === 0) return `${quando} · mesmo valor, mesmo dia`
  if (dias <= JANELA_DIAS) return `${quando} · mesmo valor, ${String(dias)}d de diferença`
  return `${quando} · mesmo valor, ${String(dias)} dias de diferença — confira a data`
}

const ESTADO: Record<Receipt['status'], string> = {
  uploaded: 'na fila para ler',
  processing: 'lendo…',
  extracted: 'lida',
  confirmed: 'confirmada',
  failed: 'a leitura falhou',
}
